import { useEffect } from "react";

/**
 * Warn before leaving when `active` is true.
 * Mirrors the Tables page leave guard: beforeunload + capture-phase link clicks.
 *
 * @param {{ active: boolean, basePath?: string, message: string, onLeave?: () => void }} opts
 *   basePath: stay on this path (and children) without confirming
 */
export function useConfirmLeave({ active, basePath, message, onLeave }) {
  useEffect(() => {
    if (!active) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    const base = (basePath || "").replace(/\/$/, "");
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (base && (url.pathname === base || url.pathname.startsWith(`${base}/`))) return;
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        onLeave?.();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [active, basePath, message, onLeave]);
}
